/**
 * The dishes of a business (docs/01-architektur.md, Datenmodell): what the menus are made of. Every
 * dish has a number of its own, unique in the business, to type it in quickly and to order by
 * („die 12 bitte“). The allergens are codes of the business's catalogue; null means nobody entered
 * them yet, an empty list that it contains none.
 */
import { cleanCategory, resolveCategory, topCategory } from "./categories.ts";
import { parseCodes } from "./allergens.ts";
import { searchScore } from "./search.ts";
import { MAX_PRICE, parsePrice, plainPrice } from "./money.ts";
import { cleanTranslations, sourceOf } from "./translation.ts";
import type { Translations, TranslationsInput } from "./translation.ts";

/**
 * A further price of a dish (S19): another size („½ Ración“, „0,4 l“) or another choice („mit Feta“).
 * Its codes add to the dish's own: „mit Thunfisch“ brings the fish (d).
 */
export interface Variant {
  label: string;
  price: number | null;
  allergens: string[];
}

/** A dish is food unless it says it is a drink (S22): drinks have a catalogue of their own in the app. */
export type DishKind = "food" | "drink";

export interface Dish {
  id: string;
  /** Only on drinks (S22); a dish without it is food, as every dish before drinks was. */
  kind?: "drink";
  number: number;
  name: string;
  description: string;
  /** „Warm / Meeresfrüchte“: free, levels split by „/“. */
  category: string;
  tags: string[];
  allergens: string[] | null;
  /** The usual price in cents; every new menu starts with it. */
  price: number | null;
  /** What the price is for („Ración“, „0,2 l“); with it the price stands on a line of its own (S19). */
  priceLabel: string;
  /** Further prices, in their order (S19). */
  variants: Variant[];
  archived: boolean;
  /** Name and description in further languages (S36); only when there are any. */
  translations?: Translations;
  /** „Name nicht übersetzen“ (S36 § 3); unset: drinks keep theirs, food not. */
  keepName?: boolean;
  /** „vom Modell, nicht geprüft“ (S41): the allergens are the model's guess until someone looks at them. */
  allergensBy?: "ai";
  version: number;
  updatedAt: number;
}

/** What a form or a row of the spreadsheet gives for a dish. */
export interface DishInput {
  kind?: DishKind;
  number?: number | null;
  name: string;
  description?: string;
  category?: string;
  tags?: string[];
  allergens?: string[] | null;
  price?: number | null;
  priceLabel?: string;
  variants?: { label: string; price?: number | null; allergens?: string[] | null }[];
  archived?: boolean;
  /** Unset: the dish keeps the translations it has (a spreadsheet without them changes none). */
  translations?: TranslationsInput | null;
  keepName?: boolean | null;
  /** "ai": guessed by the model (S41); null: looked at; unset: as it was while the codes stay the same. */
  allergensBy?: "ai" | null;
}

export const MAX_NUMBER = 99_999;
export const LIMITS = { name: 120, description: 600, tag: 40, tags: 20, priceLabel: 40, variants: 6, drinkVariants: 10 } as const;
/** Where the numbers of new drinks begin unless the business says otherwise (S22). */
export const DRINK_START = 100;

export const isDrink = (d: Pick<Dish, "kind">) => d.kind === "drink";
export const kindOf = (d: Pick<Dish, "kind">): DishKind => (d.kind === "drink" ? "drink" : "food");

const oneLine = (s: unknown) => (typeof s === "string" ? s.normalize("NFC").replace(/\s+/g, " ").trim() : "");
/** Line breaks in a description count as spaces on the card; one paragraph per dish. */
const text = oneLine;

/** Tags as entered: trimmed, each once (without regard to case), at most 20. */
export function cleanTags(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/[,;]/) : [];
  const out: string[] = [];
  for (const t of list) {
    const tag = oneLine(t).slice(0, LIMITS.tag);
    if (tag && !out.some((o) => o.toLowerCase() === tag.toLowerCase())) out.push(tag);
  }
  return out.slice(0, LIMITS.tags);
}

/**
 * A dish as it may be stored, or what is wrong with it. `known`: the codes of the allergen
 * catalogue; `categories`: the business's list (S12), the category must be one of them (without it
 * any category goes, for tools and tests); `number` must already be free (the caller checks).
 */
export function cleanDish(input: DishInput, known: ReadonlySet<string>, categories?: readonly string[]): { value: Omit<Dish, "id" | "version" | "updatedAt" | "number"> & { number: number | null } } | { error: string } {
  const name = oneLine(input.name);
  if (name === "") return { error: "Bitte einen Namen angeben." };
  if (name.length > LIMITS.name) return { error: `Der Name ist zu lang (höchstens ${LIMITS.name} Zeichen).` };
  const description = text(input.description ?? "");
  if (description.length > LIMITS.description) return { error: `Die Beschreibung ist zu lang (höchstens ${LIMITS.description} Zeichen).` };
  const number = input.number ?? null;
  if (number !== null && (!Number.isInteger(number) || number < 1 || number > MAX_NUMBER)) return { error: `Die Nummer muss eine ganze Zahl von 1 bis ${MAX_NUMBER} sein.` };
  const price = input.price ?? null;
  if (price !== null && (!Number.isInteger(price) || price < 0 || price > MAX_PRICE)) return { error: "Der Preis passt nicht." };
  let allergens: string[] | null = null;
  if (input.allergens !== undefined && input.allergens !== null) {
    const unknown = input.allergens.filter((c) => !known.has(c));
    if (unknown.length) return { error: `Unbekanntes Kürzel: ${unknown.join(", ")}` };
    allergens = [...new Set(input.allergens)];
  }
  const priceLabel = oneLine(input.priceLabel ?? "");
  if (priceLabel.length > LIMITS.priceLabel) return { error: `Die Bezeichnung des Preises ist zu lang (höchstens ${LIMITS.priceLabel} Zeichen).` };
  const drink = input.kind === "drink";
  const variants = cleanVariants(input.variants ?? [], known, drink ? LIMITS.drinkVariants : LIMITS.variants);
  if ("error" in variants) return variants;
  if (variants.value.some((v) => sameLabel(v.label, priceLabel))) return { error: `„${priceLabel}“ steht zweimal bei den Preisen.` };
  const translations = cleanTranslations(input.translations, sourceOf(name, description));
  if ("error" in translations) return translations;
  let category = cleanCategory(input.category ?? "");
  if (categories) {
    const found = resolveCategory(category, categories);
    if ("error" in found) return { error: `${found.error}. Bitte erst unter „Kategorien“ anlegen.` };
    category = found.value;
  }
  return {
    value: {
      ...(drink ? { kind: "drink" as const } : {}),
      number,
      name,
      description,
      category,
      tags: cleanTags(input.tags ?? []),
      allergens,
      price,
      priceLabel,
      variants: variants.value,
      archived: input.archived === true,
      ...(Object.keys(translations.value).length ? { translations: translations.value } : {}),
      ...(typeof input.keepName === "boolean" ? { keepName: input.keepName } : {}),
      ...(input.allergensBy === "ai" ? { allergensBy: "ai" as const } : {}),
    },
  };
}

/** The further prices as they may be stored: each with a label of its own, its codes known, at most six (ten for a drink). */
export function cleanVariants(input: readonly NonNullable<DishInput["variants"]>[number][], known: ReadonlySet<string>, max: number = LIMITS.variants): { value: Variant[] } | { error: string } {
  if (input.length > max) return { error: max === LIMITS.variants ? `Höchstens ${max} weitere Preise je Speise.` : `Höchstens ${max} weitere Preise je Getränk.` };
  const out: Variant[] = [];
  for (const v of input) {
    const label = oneLine(v.label);
    if (label === "") return { error: "Jeder weitere Preis braucht eine Bezeichnung, etwa „½ Ración“ oder „mit Feta“." };
    if (label.length > LIMITS.priceLabel) return { error: `„${label.slice(0, 20)}…“: höchstens ${LIMITS.priceLabel} Zeichen.` };
    if (out.some((o) => sameLabel(o.label, label))) return { error: `„${label}“ steht zweimal bei den Preisen.` };
    const price = v.price ?? null;
    if (price !== null && (!Number.isInteger(price) || price < 0 || price > MAX_PRICE)) return { error: `Der Preis von „${label}“ passt nicht.` };
    const codes = v.allergens ?? [];
    const unknown = codes.filter((c) => !known.has(c));
    if (unknown.length) return { error: `Unbekanntes Kürzel bei „${label}“: ${unknown.join(", ")}` };
    out.push({ label, price, allergens: [...new Set(codes)] });
  }
  return { value: out };
}

/** The same variant label: case and spaces do not count („½ Ración“ is not „Ración“). */
const labelKey = (s: string) => s.normalize("NFC").toLocaleLowerCase("de-DE").replace(/\s+/g, " ").trim();
export const sameLabel = (a: string, b: string) => labelKey(a) === labelKey(b);

/** „½ Ración 11,50; mit Feta 7,80 (g)“: the further prices as one cell of the workbook. */
export const variantsText = (variants: readonly Variant[]): string =>
  variants.map((v) => [v.label, plainPrice(v.price), v.allergens.length ? `(${v.allergens.join(", ")})` : ""].filter(Boolean).join(" ")).join("; ");

/** A price at the end of a part: „11,50“, „7,80 €“, „22 Euro“. */
const TRAILING_PRICE = /(?:^|\s)(\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?|\d+(?:[.,]\d{1,2})?(?:,-)?)\s*(?:€|eur|euro|euros)?$/i;

/**
 * The further prices from a cell: parts split by „;“ or a line break, each „Bezeichnung Preis
 * (Kürzel)“; the price and the codes may be missing. What does not fit is said by the part.
 */
export function parseVariants(raw: unknown, known: ReadonlySet<string>, max: number = LIMITS.variants): { value: Variant[] } | { error: string } {
  const text = typeof raw === "string" ? raw : raw === null || raw === undefined ? "" : String(raw);
  const out: NonNullable<DishInput["variants"]> = [];
  for (const part of text.split(/[;\n]+/).map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean)) {
    let rest = part;
    let allergens: string[] = [];
    const codes = /\(([^()]*)\)\s*$/.exec(rest);
    if (codes) {
      const parsed = parseCodes(codes[1], known);
      if (parsed.unknown.length) return { error: `„${part}“: unbekanntes Kürzel ${parsed.unknown.map((c) => `„${c}“`).join(", ")}` };
      allergens = parsed.codes ?? [];
      rest = rest.slice(0, codes.index).trim();
    }
    let price: number | null = null;
    const m = TRAILING_PRICE.exec(rest);
    if (m) {
      const p = parsePrice(m[1]);
      if ("error" in p) return { error: `„${part}“: ${p.error}` };
      price = p.value;
      rest = rest.slice(0, m.index).trim();
    }
    const label = rest.replace(/[\s:–-]+$/, "").trim();
    if (label === "") return { error: `„${part}“: Bitte eine Bezeichnung vor den Preis schreiben, etwa „½ Ración 11,50“.` };
    out.push({ label, price, allergens });
  }
  return cleanVariants(out, known, max);
}

/** Every code of a dish, its variants' included. */
export const dishCodes = (d: Pick<Dish, "allergens" | "variants">): string[] => [...new Set([...(d.allergens ?? []), ...d.variants.flatMap((v) => v.allergens)])];

/**
 * The next free number: one above the highest. With a kind (S22): one above the highest of that kind,
 * a drink at least at `drinkStart`; a number another dish or drink has is skipped (numbers stay unique).
 */
export const nextNumber = (dishes: Iterable<Pick<Dish, "number" | "kind">>, kind?: DishKind, drinkStart = DRINK_START) => {
  const all = [...dishes];
  let max = kind === "drink" ? Math.max(0, drinkStart - 1) : 0;
  for (const d of all) if (kind === undefined || kindOf(d) === kind) max = Math.max(max, d.number);
  const taken = new Set(all.map((d) => d.number));
  let n = Math.min(max + 1, MAX_NUMBER);
  while (taken.has(n) && n < MAX_NUMBER) n++;
  return n;
};

/**
 * The amount a price label names, in millilitres (S22): „0,3 l“ 300, „1/8“ 125, „4 cl“ 40, „250 ml“,
 * „Flasche 0,75 l“ 750, „Maß“ 1000, „Halbe“ 500; null when it names none („Ración“, „mit Feta“).
 */
export function mlOf(label: string): number | null {
  const t = label.normalize("NFC").toLowerCase().replace(/\u00a0/g, " ");
  const fraction = /(?:^|[^\d,.])(\d{1,2})\s*\/\s*(\d{1,2})(?:\s*(?:l|liter)\b)?/.exec(t);
  if (fraction && Number(fraction[2]) > 0) return Math.round((1000 * Number(fraction[1])) / Number(fraction[2]));
  const amount = /(\d+(?:[.,]\d+)?)\s*(ml|cl|l|ltr|liter)\b/.exec(t);
  if (amount) {
    const v = Number(amount[1]!.replace(",", "."));
    const ml = amount[2] === "ml" ? v : amount[2] === "cl" ? v * 10 : v * 1000;
    return ml > 0 && ml <= 100_000 ? Math.round(ml) : null;
  }
  if (/(^|\s)ma(ß|ss)(\s|$)/.test(t)) return 1000;
  if (/\bhalbe\b/.test(t)) return 500;
  return null;
}

/** The labels of a dish's prices in their order: the main one (when it has a label) and the further ones. */
export const priceLabelsOf = (d: Pick<Dish, "priceLabel" | "variants">): string[] => [...(d.priceLabel ? [d.priceLabel] : []), ...d.variants.map((v) => v.label)];

/** Whether a drink has a price without an amount (S22: the amount belongs to the price of a drink). */
export function lacksAmount(d: Pick<Dish, "price" | "priceLabel" | "variants">): boolean {
  // A cup of coffee is sold by the cup.
  const named = (label: string) => mlOf(label) !== null || /\b(tasse|kännchen|kaennchen|pott|becher)\b/i.test(label);
  if (d.price !== null && !named(d.priceLabel)) return true;
  return d.variants.some((v) => v.price !== null && !named(v.label));
}

/** The text the search looks in: number first, so „12“ finds the 12; then name, category, tags, description. */
export const searchTextOf = (d: Pick<Dish, "number" | "name" | "category" | "tags" | "description">) => `${d.number} nr${d.number} ${d.name} ${d.category} ${d.tags.join(" ")} ${d.description}`;

/**
 * The dishes that fit a query, best first: the dish with exactly that number before all others, then
 * by how well the words fit (typos and accents forgiven), then by number.
 */
export function findDishes<T extends Pick<Dish, "number" | "name" | "category" | "tags" | "description">>(dishes: Iterable<T>, query: string): T[] {
  const q = query.trim();
  const all = [...dishes];
  if (q === "") return all.sort((a, b) => a.number - b.number);
  const exact = /^\d+$/.test(q) ? Number(q) : null;
  const hits: { d: T; s: number }[] = [];
  for (const d of all) {
    if (d.number === exact) {
      hits.push({ d, s: -10 });
      continue;
    }
    // A number alone finds only its dish and those whose number starts with it (12 → 120, 121 …).
    if (exact !== null) {
      if (String(d.number).startsWith(q)) hits.push({ d, s: -5 });
      continue;
    }
    const nameScore = searchScore(`${d.name}`, q);
    const s = nameScore !== null ? nameScore - 1 : searchScore(searchTextOf(d), q);
    if (s !== null) hits.push({ d, s });
  }
  return hits.sort((x, y) => x.s - y.s || x.d.number - y.d.number).map((h) => h.d);
}

/** Every tag in the catalogue, most used first. */
export function allTags(dishes: Iterable<Pick<Dish, "tags" | "archived">>): string[] {
  const count = new Map<string, { tag: string; n: number }>();
  for (const d of dishes) {
    if (d.archived) continue;
    for (const t of d.tags) {
      const key = t.toLowerCase();
      const c = count.get(key);
      if (c) c.n++;
      else count.set(key, { tag: t, n: 1 });
    }
  }
  return [...count.values()].sort((a, b) => b.n - a.n || a.tag.localeCompare(b.tag, "de")).map((c) => c.tag);
}

/** The first levels of the categories the dishes use, in the order of the list (else of the numbers). */
export function allCategories(dishes: Iterable<Pick<Dish, "category" | "number" | "archived">>, order: readonly string[] = []): string[] {
  const out: string[] = [];
  for (const d of [...dishes].sort((a, b) => a.number - b.number)) {
    if (d.archived || !d.category) continue;
    const top = topCategory(d.category);
    if (!out.includes(top)) out.push(top);
  }
  const tops = order.map(topCategory);
  const rank = (t: string) => (tops.includes(t) ? tops.indexOf(t) : tops.length);
  return out.map((t, i) => ({ t, i })).sort((a, b) => rank(a.t) - rank(b.t) || a.i - b.i).map((x) => x.t);
}

/**
 * Allergens to start a new drink with, by its category (S22): sulphites for wine, sparkling wine and
 * cider, gluten for beer – the codes of the business's catalogue whose names say so; null: none known.
 */
export function suggestedAllergens(category: string, catalog: readonly { code: string; name: string; kind: string }[]): string[] | null {
  const c = category.toLowerCase();
  const find = (re: RegExp) => {
    const codes = catalog.filter((a) => a.kind === "allergen" && re.test(a.name.toLowerCase())).map((a) => a.code).slice(0, 1);
    return codes.length ? codes : null;
  };
  if (/wein|sekt|champagner|prosecco|cava|crémant|cremant|cidre|cider|wermut|sherry|portwein|sangr/.test(c)) return find(/sulfit|schwefel/);
  if (/bier|pils|weizen|radler|helles|ale\b|stout|bock/.test(c)) return find(/gluten/);
  return null;
}

/** Tags that tell guests about the diet; shown on the card unless the menu says otherwise. */
export const DIET_TAGS = ["vegan", "vegetarisch", "glutenfrei", "laktosefrei", "scharf"] as const;
export const isDietTag = (tag: string) => (DIET_TAGS as readonly string[]).includes(tag.toLowerCase());
